'use client';

import React, { useState, useEffect } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { RegisterBusinessMutationVariables, useRegisterBusinessMutation } from 'Generated/graphql';
import { Spinner } from 'theme-ui';
import { AppRoutePath } from 'Pages/routes';
import { useRouter, useSearchParams } from 'next/navigation';
import signinWithIdToken from 'Common/functions/signinWithIdToken';
import { SearchParam } from 'Common/functions/Search';
import Link from 'next/link';
import { useApolloClient } from '@apollo/client';
import { getBusinessSignupFormFormikConfig } from './fn';
import FirebaseAuthStage from 'Pages/signup/stages/FirebaseAuthStage';
import OfficeBuilding from 'Images/svg/office-building.svg';
import ResidentialBuilding from 'Images/svg/residential-building.svg';
import FirebaseClient from 'Clients/FireBaseClient';
import { BusinessNumEmployessInput, BusinessPropertyNameInput } from 'Pages/signup/stages/AccountDetailsStage/functions';
import PhoneNumberField from 'Common/components/PhoneNumberField';

// Enum for step types
enum SignupStep {
  SELECT_LOCATION,
  PROFILE_INFO,
  FIREBASE_AUTH,
}

// Types
type BusinessType = 'office' | 'residential-property';

interface ContentProps {
  step: SignupStep;
  tosChecked: boolean;
  tosError: boolean;
  onClick: () => void;
  setTosChecked: (checked: boolean) => void;
  onSelectBusinessType: (t: BusinessType) => void;
  businessType: BusinessType;
  phoneNumber: string;
  setPhoneNumber: (s: string) => void;
}

const LocationTypeCard: React.FC<{ type: BusinessType; icon: React.ReactNode; label: string; onClick: (t: BusinessType) => void }> = ({ type, icon, label, onClick }) => (
  <div className="card justify-between items-center basis-1/2 sm:text-sm sm:min-w-40 lg:min-w-52 cursor-pointer" onClick={() => onClick(type)}>
    {icon}
    <span>{label}</span>
  </div>
);

const LocationTypeSelection: React.FC<{ onSelectBusinessType: (t: BusinessType) => void }> = ({ onSelectBusinessType }) => (
  <div>
    <span className="heading">Select Location Type</span>
    <div className="flex sm:flex-col lg:flex-row gap-4">
      <LocationTypeCard
        type="office"
        icon={<OfficeBuilding className="w-32 h-48" />}
        label="Company Office"
        onClick={onSelectBusinessType}
      />
      <LocationTypeCard
        type="residential-property"
        icon={<ResidentialBuilding className="w-32 h-48" />}
        label="Residential Building(s)"
        onClick={onSelectBusinessType}
      />
    </div>
  </div>
);

const ProfileInformation: React.FC<{ businessType: BusinessType; phoneNumber: string; setPhoneNumber: (s: string) => void }> = ({ businessType, phoneNumber, setPhoneNumber }) => (
  <div className="flex flex-col justify-center items-start">
    <span className="heading">Profile Information</span>
    <div className="flex flex-col w-full my-4 gap-4">
      <BusinessPropertyNameInput label={businessType === 'office' ? 'Business Name' : 'Residential Building Name'} />
      <BusinessNumEmployessInput />
      <PhoneNumberField value={phoneNumber} label="Phone #" onChange={setPhoneNumber} />
    </div>
  </div>
);

const FirebaseAuthWrapper: React.FC<{ tosChecked: boolean; tosError: boolean; setTosChecked: (checked: boolean) => void; onClick: () => void }> = ({ tosChecked, tosError, setTosChecked, onClick }) => (
  <div className="flex flex-col max-w-[390px]" data-testid="signupForm_firebaseAuthStageWrap" data-cy="signupForm_firebaseAuthStageWrap" onClick={onClick}>
    <FirebaseAuthStage tosChecked={tosChecked} setTosChecked={setTosChecked} />
    {tosError && !tosChecked && <span className="error">You must accept the terms to continue.</span>}
  </div>
);

const getContent = ({ step, ...props }: ContentProps) => {
  switch (step) {
    case SignupStep.SELECT_LOCATION:
      return <LocationTypeSelection onSelectBusinessType={props.onSelectBusinessType} />;
    case SignupStep.PROFILE_INFO:
      return <ProfileInformation businessType={props.businessType} phoneNumber={props.phoneNumber} setPhoneNumber={props.setPhoneNumber} />;
    case SignupStep.FIREBASE_AUTH:
      return <FirebaseAuthWrapper {...props} />;
  }
};

const isButtonDisabled = (values: RegisterBusinessMutationVariables, currentStep: SignupStep): boolean => {
  return currentStep === SignupStep.PROFILE_INFO && (!values.business.name || !values.business.numEmployees);
};

// Main component
const BusinessSignup: React.FC<{ isLoading?: boolean }> = ({ isLoading = false }) => {
  const [step, setStep] = useState<SignupStep>(SignupStep.SELECT_LOCATION);
  const { setFieldValue, values } = useFormikContext<RegisterBusinessMutationVariables>();
  const [tosChecked, setTosChecked] = useState(false);
  const [tosError, setTosError] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');

  useEffect(() => {
    const timezoneLocation = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setFieldValue('timezoneLocation', timezoneLocation);
  }, [setFieldValue]);

  const handleContinue = () => {
    if (isButtonDisabled(values, step)) {return;}
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const handleSelectBusinessType = (type: BusinessType) => {
    setFieldValue('business.type', type);
    setStep(SignupStep.PROFILE_INFO);
  };

  return (
    <Form className="flex justify-center w-full max-w-full">
      <div className="flex flex-col items-center gap-4 w-full">
        {isLoading ? (
          <div className="flex">
            <Spinner size={32} />
          </div>
        ) : (
          getContent({
            step,
            tosChecked,
            tosError,
            onClick: () => setTosError(!tosChecked),
            setTosChecked,
            onSelectBusinessType: handleSelectBusinessType,
            businessType: values.business.type as BusinessType,
            phoneNumber,
            setPhoneNumber: (p) => {
              setPhoneNumber(p);
              setFieldValue('phoneNumber', p);
            },
          })
        )}
        {step > SignupStep.SELECT_LOCATION && step < SignupStep.FIREBASE_AUTH && (
          <span className="link" onClick={handleBack}>
            Back
          </span>
        )}
        {step !== SignupStep.FIREBASE_AUTH && (
          <button type="button" className="btn-primary" id="signup-commoninputs-continue" onClick={handleContinue}>
            Continue
          </button>
        )}
        {!isLoading && (
          <div className="flex flex-col justify-start gap-4">
            <span className="text-sm">
              Already have an account?{' '}
              <Link href={AppRoutePath.LOGIN} className="link">
                Log In
              </Link>
              .
            </span>
          </div>
        )}
      </div>
    </Form>
  );
};

const BusinessSignupWithFormik: React.FC<{ isLoading?: boolean }> = (props) => {
  const router = useRouter();
  const search = useSearchParams();
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [registerBusiness] = useRegisterBusinessMutation();

  const getRedirectAfterSignupPath = () => search.get(SearchParam.RETURN_TO) as string || AppRoutePath.BUSINESS_SCHEDULE;

  const handleSubmit = async (data: RegisterBusinessMutationVariables) => {
    setIsLoading(true);
    try {
      await registerBusiness({
        variables: {
          idToken: data.idToken,
          timezoneLocation: data.timezoneLocation,
          phoneNumber: data.phoneNumber,
          business: {
            name: data.business.name,
            numEmployees: data.business.numEmployees?.toString(),
            type: data.business.type,
          },
        },
      });

      const updatedToken = await FirebaseClient.getCurrentUser()?.getIdToken(true);
      if (updatedToken) {
        await signinWithIdToken(updatedToken, true, (userData) => {
          client.resetStore().finally(() => {
            const redirect = getRedirectAfterSignupPath();
            router.push(userData.fullName ? redirect : `${AppRoutePath.UPDATE_USERNAME}?${SearchParam.RETURN_TO}=${redirect}`);
          });
        });
      }
    } catch (error) {
      console.error('Error during business registration:', error);
      // Handle error (e.g., show error message to user)
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Formik {...getBusinessSignupFormFormikConfig(handleSubmit)}>
      <BusinessSignup isLoading={isLoading} {...props} />
    </Formik>
  );
};

export default BusinessSignupWithFormik;
